import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Auth, signInWithEmailAndPassword, signInWithPopup, OAuthProvider, signOut, authState, User } from '@angular/fire/auth';
import { Observable, of } from 'rxjs';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private auth = inject(Auth, { optional: true });
  private router = inject(Router);

  readonly currentUser$: Observable<User | null> = this.auth ? authState(this.auth) : of(null);

  /**
   * Password sign-in exists for local + dev development only; Okta is the sign-in path everywhere
   * else. This check keeps the disabled environments from calling Firebase at all, but it is a
   * convenience gate, not a control - the authoritative one is leaving the Email/Password provider
   * disabled in the qa/staging/prod Firebase projects, since the web API key is public.
   */
  signIn(email: string, password: string) {
    if (!this.auth) return Promise.reject(new Error('Firebase is not configured.'));
    if (!environment.allowPasswordSignIn) {
      return Promise.reject(new Error('Email and password sign-in is disabled in this environment. Please sign in with Okta.'));
    }
    return signInWithEmailAndPassword(this.auth, email, password);
  }

  /**
   * Okta is registered as an OIDC provider inside this environment's Firebase project, so the
   * provider id is per-project and comes from the environment rather than being hardcoded.
   * Firebase requires the `oidc.` prefix on it.
   *
   * Popup rather than redirect deliberately: every environment's authDomain is a
   * `*.firebaseapp.com` domain, and signInWithRedirect breaks there when the browser blocks
   * third-party storage. Popup has no such dependency.
   */
  async signInWithOkta() {
    if (!this.auth) throw new Error('Firebase is not configured.');

    const providerId = environment.oktaProviderId;
    if (!providerId) {
      throw new Error('Okta sign-in is not configured for this environment.');
    }
    if (!providerId.startsWith('oidc.')) {
      // OAuthProvider throws an opaque auth/argument-error without the prefix - say why instead.
      throw new Error(`Invalid Okta provider id "${providerId}" - it must start with "oidc.".`);
    }

    const provider = new OAuthProvider(providerId);
    // Authorization resolves users by email on both sides (BrandAccessMiddleware on the API,
    // BrandContextService here), so the email claim is load-bearing. Requested explicitly rather
    // than relying on whatever Firebase's default OIDC scope set happens to be.
    provider.addScope('email');
    provider.addScope('profile');

    const credential = await signInWithPopup(this.auth, provider);

    // Fail closed and loudly. Without an email claim the user would sign in successfully, pass the
    // route guard, and land on an app scoped to zero brands with nothing explaining why - so treat
    // a missing email as a failed sign-in rather than as an account with no permissions.
    if (!credential.user.email) {
      await signOut(this.auth);
      throw new Error('Okta did not return an email address for your account, which is required for access. Please contact your admin.');
    }

    return credential;
  }

  async signOut() {
    if (this.auth) await signOut(this.auth);
    this.router.navigate(['/login']);
  }

  getCurrentUser(): User | null {
    return this.auth?.currentUser ?? null;
  }
}
